import {
  PlannedOccurrence,
  ScheduleEntryState,
  Task,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { isBetween } from './lib/date-fns';
import {
  getActiveSchedule,
  getActualScheduleStartDate,
  getSchedulePlanning,
  getScheduleTimeframeByDate,
  getTimeframeSchedules,
} from './schedules';

export type GetTaskPlanningOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
  strictEndDate?: boolean;
};

export const getTaskPlanning = (
  task: Task,
  {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
    strictEndDate,
  }: GetTaskPlanningOptions,
): PlannedOccurrence[] => {
  if (!task.schedules?.length) {
    const deadline = task.endStrategy?.deadline;
    return ((uncompletedOnly || sinceLastEntryOnly) && task.completedAt) ||
      !deadline ||
      !isBetween(deadline, startDate, endDate)
      ? []
      : [
          {
            id: uuidv4(),
            date: deadline,
            timeslot: task.timeslot ?? null,
          },
        ];
  }

  const timeframeSchedules = getTimeframeSchedules(task.schedules, {
    startDate,
    endDate,
  });

  return timeframeSchedules.reduce<PlannedOccurrence[]>((acc, schedule) => {
    acc.push(
      ...getSchedulePlanning(schedule, {
        startDate,
        endDate,
        today,
        weekStartsOn,
        uncompletedOnly,
        sinceLastEntryOnly,
        strictEndDate,
      }),
    );
    return acc;
  }, []);
};

export type GetDueTaskPlanningOptions = Omit<
  GetTaskPlanningOptions,
  'startDate' | 'endDate' | 'sinceLastEntryOnly' | 'strictEndDate'
> & {
  date: Timestamp;
};

export const getDueTaskPlanning = (
  task: Task,
  { date, today, weekStartsOn, uncompletedOnly }: GetDueTaskPlanningOptions,
): PlannedOccurrence[] => {
  if (task.completedAt) {
    return [];
  }
  // if the task has no schedules, the planning will return the deadline in the correct format
  if (!task.schedules?.length) {
    const planning = getTaskPlanning(task, {
      startDate: startOfDay(date),
      endDate: endOfDay(date),
      today,
      weekStartsOn,
      uncompletedOnly,
    });

    if (planning.length) {
      return planning;
    }

    // if the task is not due on the date, but it is overdue, we still want to return it
    const deadline = task.endStrategy?.deadline;
    if (deadline && isBefore(deadline, startOfDay(date))) {
      return [
        {
          id: uuidv4(),
          date: deadline,
          timeslot: task.timeslot ?? null,
        },
      ];
    }

    return [];
  }

  const getFirstUpcomingOccurrence = (): PlannedOccurrence[] => {
    const upcomingSchedule = task
      .schedules!.filter(({ startDate: scheduleStart }) =>
        isAfter(scheduleStart, date),
      )
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())[0];

    if (!upcomingSchedule) {
      return [];
    }

    const upcomingTimeframe = getScheduleTimeframeByDate(
      upcomingSchedule,
      upcomingSchedule.startDate,
      weekStartsOn,
    );

    const planning = getSchedulePlanning(upcomingSchedule, {
      startDate: upcomingTimeframe.startDate,
      endDate: upcomingTimeframe.endDate,
      today: upcomingSchedule.startDate,
      weekStartsOn,
    });

    return planning.length ? [planning[0]] : [];
  };

  // ensure the startDate starts in the timeframe of the today
  const activeSchedule = getActiveSchedule(task.schedules, today);
  if (!activeSchedule) {
    return getFirstUpcomingOccurrence();
  }
  const timeframe = getScheduleTimeframeByDate(
    activeSchedule,
    date,
    weekStartsOn,
  );
  // get the previous timeframe to display the overdue entry if there is one
  const prevTimeframe = getScheduleTimeframeByDate(
    activeSchedule,
    addDays(timeframe.startDate, -1),
    weekStartsOn,
  );

  const endOfToday = endOfDay(today);
  const _startDate = prevTimeframe.startDate;
  // get the latest date of today and timeframe, as today can increase the range, and it can mean that an overdue occurrence can be found
  const _endDate = new Date(
    Math.max(endOfToday.getTime(), timeframe.endDate.getTime()),
  );

  const allPlanning = getTaskPlanning(task, {
    startDate: _startDate,
    endDate: _endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
  });

  // Determine the last valid entry date
  const entryDateTimes = task.schedules.reduce<number[]>((acc, schedule) => {
    schedule.entries.forEach(({ date, value, state }) => {
      const isFrequency = !!schedule.frequency?.count;
      if (
        isFrequency &&
        isBetween(date, _startDate, _endDate) &&
        ((state === ScheduleEntryState.Complete && !!value) ||
          state !== ScheduleEntryState.Complete)
      ) {
        acc.push(date.getTime());
      }
      if (
        !isFrequency &&
        isBetween(date, _startDate, _endDate) &&
        ((state === ScheduleEntryState.Complete &&
          // with a rrule schedule, it depends on if it is due on the day it is shown.
          // today is due when it is not fully completed yet, but it is not due on a different day when there is a completion value
          (isSameDay(date, today)
            ? (value ?? 0) >= schedule.completionTarget.count
            : !!value)) ||
          state !== ScheduleEntryState.Complete)
      ) {
        acc.push(date.getTime());
      }
    });

    return acc;
  }, []);

  // get the last entry date before today (if there is one),
  // to determine if the task is due today or overdue (without considering future entries)
  const entryDateTimesBeforeToday = entryDateTimes.filter(
    (date) => !isAfter(date, endOfToday),
  );
  const lastScheduleEntryDateBeforeToday = entryDateTimesBeforeToday.length
    ? new Date(Math.max(...entryDateTimesBeforeToday))
    : undefined;

  // get the past, future and today occurrences
  const {
    past,
    today: todayOccurrences,
    future,
  } = allPlanning.reduce(
    (acc, occurrence) => {
      // actual start, not nominal startDate, so a pre-start planned occurrence still surfaces
      if (
        isBefore(
          occurrence.date,
          startOfDay(getActualScheduleStartDate(activeSchedule)),
        )
      ) {
        return acc;
      }
      // Filter for valid occurrences (ones without entries)
      if (
        !lastScheduleEntryDateBeforeToday ||
        (isAfter(occurrence.date, lastScheduleEntryDateBeforeToday) &&
          !isSameDay(occurrence.date, lastScheduleEntryDateBeforeToday)) ||
        (isAfter(occurrence.date, endOfToday) &&
          entryDateTimes.some((date) => isSameDay(date, occurrence.date)))
      ) {
        // Partition the valid occurrence into the correct bucket.
        if (
          // completion counts higher than 1 means it cannot be overdue (is-schedule-overdue)
          !(activeSchedule.completionTarget.count > 1) &&
          isBefore(occurrence.date, today)
        ) {
          acc.past.push(occurrence);
          return acc;
        }

        if (isSameDay(occurrence.date, today)) {
          acc.today.push(occurrence);
          return acc;
        }

        if (isAfter(occurrence.date, today)) {
          acc.future.push(occurrence);
          return acc;
        }
      }

      return acc;
    },
    { past: [], today: [], future: [] } as {
      past: PlannedOccurrence[];
      today: PlannedOccurrence[];
      future: PlannedOccurrence[];
    },
  );

  // inject ancient uncompleted planned occurrences (those before the search window)
  if (activeSchedule.plannedOccurrences?.length) {
    const searchStartDate = _startDate;

    const lastEntryDate = activeSchedule.entries.reduce<Date | undefined>(
      (acc, { date, state, value }) => {
        if (
          (state === ScheduleEntryState.Complete && !!value) ||
          state !== ScheduleEntryState.Complete
        ) {
          return !acc || isAfter(date, acc) ? date : acc;
        }
        return acc;
      },
      undefined,
    );

    const ancientOccurrences = activeSchedule.plannedOccurrences
      .filter(
        (plannedOccurrence) =>
          isBefore(plannedOccurrence.date, searchStartDate) &&
          (!lastEntryDate || isAfter(plannedOccurrence.date, lastEntryDate)),
      )
      .map((plannedOccurrence) => ({
        id: plannedOccurrence.id,
        date: plannedOccurrence.date,
        timeslot: plannedOccurrence.timeslot ?? activeSchedule.timeslot ?? null,
      }));

    past.push(...ancientOccurrences);
  }

  const latestPastDate =
    !todayOccurrences.length && past.length
      ? new Date(Math.max(...past.map(({ date }) => date.getTime())))
      : undefined;

  const result = todayOccurrences.length
    ? todayOccurrences
    : past.length
      ? // of there are past occurrences, only include the last one
        past.filter(({ date }) => isSameDay(date, latestPastDate!))
      : future;

  // If the active schedule hasn't started yet AND nothing surfaced from the
  // active-schedule path (no ancient plannedOccurrences, no in-window entries),
  // fall back to the first upcoming occurrence so a not-yet-started habit
  // still appears as upcoming.
  if (!result.length && isAfter(activeSchedule.startDate, date)) {
    return getFirstUpcomingOccurrence();
  }

  return result;
};
